import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import debounce from 'lodash/debounce';
// redux
import { deletePassengersById, getPassengers } from 'redux/passengers/operations';
import { selectPassengersList, selectPassengersTotal } from 'redux/passengers/passengersSelectors';
// hooks
import { useAuth } from 'hooks';
// helpers
import { compareStrings, isAvailableArray, setPageTitle } from 'helpers';
// components
import Modal from 'components/Modal/Modal';
import AuthBox from 'components/AuthBox/AuthBox';
import SubmitMsg from 'components/SubmitMsg/SubmitMsg';
import Datepicker from 'components/Datepicker/Datepicker';
import Pagination from 'components/Pagination/Pagination';
import PassengersList from 'components/PassengersList/PassengersList';
import PassengerAddForm from 'components/forms/PassengerForms/PassengerAddForm';
import PassengerEditForm from 'components/forms/PassengerForms/PassengerEditForm';
// scss
import './PassengersPage.scss';

const PassengersPage = () => {
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [searchString, setSearchString] = useState(null);
  const [selectedPassengersIds, setSelectedPassengersIds] = useState([]);
  const [selectedPassengerData, setSelectedPassengerData] = useState(null);
  const [filterDateActive, setFilterDateActive] = useState(false);
  const [filterDateStart, setFilterDateStart] = useState(null);
  const [filterDateEnd, setFilterDateEnd] = useState(null);
  const passengersList = useSelector(selectPassengersList);
  const passengersTotal = useSelector(selectPassengersTotal);
  const dispatch = useDispatch();
  // const navigate = useNavigate();
  const { isSuperAdmin } = useAuth();

  const totalPages = Math.ceil(passengersTotal / limit); // Загальна кількість сторінок
  const totalSelectedPassengers = passengersList?.filter(passenger => selectedPassengersIds.includes(passenger?.passenger_id))?.length || null;
  const totalSelectedTickets = passengersList?.filter(passenger => selectedPassengersIds.includes(passenger?.passenger_id))?.map(i => i?.number_of_tickets)?.reduce((a, b) => a + b, 0) || null;

  let totalPassengers = totalSelectedPassengers;
  let totalTickets = totalSelectedTickets;
  if (selectedPassengersIds?.length < 1 && filterDateStart) {
    totalPassengers = passengersList?.length;
    totalTickets = passengersList?.map(i => i?.number_of_tickets)?.reduce((a, b) => a + b, 0);
  }

  const isEditBtnDisabled = isSuperAdmin ? selectedPassengersIds?.length !== 1 : true;
  const isDeleteBtnDisabled = isSuperAdmin ? selectedPassengersIds?.length < 1 : true;
  const isGenerateListBtnDisabled = isSuperAdmin ? selectedPassengersIds?.length < 1 : true;
  const isGenerateTicketBtnDisabled = isSuperAdmin ? selectedPassengersIds?.length < 1 : true;
  // const calendarBtnText = filterDateStart ? formatDateForInput(filterDateStart) : "Календар";
  const calendarBtnText = filterDateStart ? `${formatDateForInput(filterDateStart)} ${filterDateEnd ? ` ${formatDateForInput(filterDateEnd)}` : ""}` : "Календар";
  const submitMsgText = `Ви впевнені, що хочете видалити ${selectedPassengersIds?.length > 1 ? "пасажирів" : "пасажира"} ?`

  function setModal(newModalType) {
    if (!newModalType) {
      setModalType(null);
      setShowModal(false);
      return;
    }

    switch (newModalType) {
      case 'PassengerAddForm':
        setModalType('PassengerAddForm');
        break;
      case 'PassengerEditForm':
        setModalType('PassengerEditForm');
        break;
      case 'PassengersDeleteModal':
        setModalType('PassengersDeleteModal');
        break;

      default:
        break;
    }

    setShowModal(true);
  }
  // === pagination ===
  function setNewPage(newPage) {
    setPage(newPage);
    dispatch(getPassengers({page: newPage, limit, search: searchString, startDay: filterDateStart, endDay: filterDateEnd }));
  }
  function setNewLimit(newLimit) {
    setLimit(newLimit);
    dispatch(getPassengers({page: 1, limit: newLimit, search: searchString, startDay: filterDateStart, endDay: filterDateEnd }));
    setPage(1);
  }
  // === pagination ===
  // === search ===
  const debouncedSearch = debounce((searchValue) => {
    dispatch(getPassengers({ page: 1, limit, search: searchValue, startDay: filterDateStart, endDay: filterDateEnd }));
  }, 1000);
  function onSearch(value) {
    if (value?.length > 0 && value?.trim()?.length === 0) return
    debouncedSearch(value);
    setSearchString(value);
    setPage(1);
  }
  // === search ===
  // === edit Passenger ===
  function onPassengerEdit() {
    const selectedPassengerId = isAvailableArray(selectedPassengersIds)[0];
    const newSelectedPassengerData = passengersList?.find(i => compareStrings(i?.passenger_id, selectedPassengerId));
    setSelectedPassengerData(newSelectedPassengerData);
    setModal('PassengerEditForm');
  }
  // === edit Passenger ===
  // === delete Passengers ===
  function onPassengersDelete() {
    dispatch(deletePassengersById(selectedPassengersIds));
    setSelectedPassengersIds([]);
    setPage(1);
    setModal()
  }
  // === delete Passengers ===
  function onClickSetDate(data) {
    const { startDay, endDay } = data ?? {};
    // if (!startDay) return setFilterDateActive(false);
    dispatch(getPassengers({ page: 1, limit, search: searchString, startDay, endDay }));
    setFilterDateActive(false);
    setFilterDateStart(startDay);
    setFilterDateEnd(endDay);
    setPage(1);
  }

  function generateTickets() {
    // Відфільтровані дані
    const generateTicketsData = passengersList.filter(passenger => 
      selectedPassengersIds.includes(passenger.passenger_id)
    );

    // dispatch(setGenerateTicketsData(generateTicketsData));
    // navigate('/tickets');

    sessionStorage.setItem('appStatetTickets', JSON.stringify(generateTicketsData));
    window.open('/tickets', '_blank');
  }

  function generatePassengersList() {
    // Відфільтровані дані
    const generatePassengersList = passengersList.filter(passenger => 
      selectedPassengersIds.includes(passenger.passenger_id)
    );

    const data = {
      passengersList: generatePassengersList,
      totalPassengers,
      totalTickets,
      calendarBtnText,
    }

    // dispatch(setGeneratePassengersList(data));
    // navigate('/passengers-for-print');

    sessionStorage.setItem('appStatePassengers', JSON.stringify(data));
    window.open('/passengers-for-print', '_blank');
  }

  useEffect(() => { setPageTitle("Інформація про пасажирів") }, []);

  return (
    <>
      <section className="passengers">
        <div className="passengers__container container">
        <div className="passengers-content">
            <div className="passengers-content__header">
              <div className="passengers__title-wrap">
                <h1 className="passengers__title">Інформація про пасажирів</h1>
              </div>
              <div className="passengers__btn-wrap">
                <button className="passengers__btn passengers__btn--calendar" type="button" onClick={() => setFilterDateActive(true)}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 22 22" fill="none" >
                    <path d="M1 7.66667H21M5.44444 1V3.22222M16.5556 1V3.22222M4.33333 11H6.55556M9.88889 11H12.1111M15.4444 11H17.6667M4.33333 14.3333H6.55556M9.88889 14.3333H12.1111M15.4444 14.3333H17.6667M4.33333 17.6667H6.55556M9.88889 17.6667H12.1111M15.4444 17.6667H17.6667M4.55556 21H17.4444C18.689 21 19.3113 21 19.7867 20.7578C20.2048 20.5448 20.5448 20.2048 20.7578 19.7867C21 19.3113 21 18.689 21 17.4444V6.77778C21 5.53321 21 4.91093 20.7578 4.43558C20.5448 4.01743 20.2048 3.67748 19.7867 3.46443C19.3113 3.22222 18.689 3.22222 17.4444 3.22222H4.55556C3.311 3.22222 2.68871 3.22222 2.21336 3.46443C1.79521 3.67748 1.45526 4.01743 1.24221 4.43558C1 4.91093 1 5.53321 1 6.77778V17.4444C1 18.689 1 19.3113 1.24221 19.7867C1.45526 20.2048 1.79521 20.5448 2.21336 20.7578C2.68871 21 3.31099 21 4.55556 21Z" strokeWidth="1.5" strokeLinecap="round" />
                  </svg>
                  {calendarBtnText}
                </button>
                {filterDateActive && <Datepicker inputName={"departure_date"} startDayInit={filterDateStart} endDayInit={filterDateEnd} handleSetDate={onClickSetDate} handleCancel={() => setFilterDateActive(false)} />}
                <button className="passengers__btn" type="button" disabled={isEditBtnDisabled} onClick={onPassengerEdit}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 17 17" fill="none" >
                    <path d="M10.2701 2.55899L10.9652 1.86384C12.117 0.712055 13.9844 0.712055 15.1362 1.86384C16.2879 3.01561 16.2879 4.88301 15.1362 6.03479L14.441 6.72994M10.2701 2.55899C10.2701 2.55899 10.3569 4.03621 11.6604 5.33963C12.9638 6.64304 14.441 6.72994 14.441 6.72994M10.2701 2.55899L8.50001 4.32904M14.441 6.72994L10.4955 10.6754L8.17098 13L8.05008 13.1209C7.61718 13.5537 7.40081 13.7702 7.16216 13.9563C6.88062 14.1759 6.57602 14.3642 6.25374 14.5178C5.98053 14.648 5.69015 14.7447 5.10939 14.9383L2.64844 15.7586M2.64844 15.7586L2.04688 15.9592C1.76109 16.0544 1.446 15.98 1.23297 15.767C1.01995 15.554 0.945572 15.2389 1.04084 14.9531L1.24136 14.3516M2.64844 15.7586L1.24136 14.3516M1.24136 14.3516L2.06167 11.8906C2.25526 11.3099 2.35205 11.0195 2.48226 10.7462C2.63586 10.424 2.8241 10.1194 3.04368 9.83784C3.22981 9.59919 3.44625 9.38281 3.87912 8.94991L5.87501 6.95403" strokeWidth="1.5" strokeLinecap="round" />
                  </svg>
                  Редагувати
                </button>
                <button className="passengers__btn" type="button" disabled={isDeleteBtnDisabled} onClick={() => setModal("PassengersDeleteModal")}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none" >
                    <path d="M13.3333 4.99996V4.33329C13.3333 3.39987 13.3333 2.93316 13.1517 2.57664C12.9919 2.26304 12.7369 2.00807 12.4233 1.84828C12.0668 1.66663 11.6001 1.66663 10.6667 1.66663H9.33333C8.39991 1.66663 7.9332 1.66663 7.57668 1.84828C7.26308 2.00807 7.00811 2.26304 6.84832 2.57664C6.66667 2.93316 6.66667 3.39987 6.66667 4.33329V4.99996M8.33333 9.58329V13.75M11.6667 9.58329V13.75M2.5 4.99996H17.5M15.8333 4.99996V14.3333C15.8333 15.7334 15.8333 16.4335 15.5608 16.9683C15.3212 17.4387 14.9387 17.8211 14.4683 18.0608C13.9335 18.3333 13.2335 18.3333 11.8333 18.3333H8.16667C6.76654 18.3333 6.06647 18.3333 5.53169 18.0608C5.06129 17.8211 4.67883 17.4387 4.43915 16.9683C4.16667 16.4335 4.16667 15.7334 4.16667 14.3333V4.99996" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  Видалити
                </button>
              </div>
              <div className="search-box__wrap">
                <div className="search-box">
                  <input
                    className="passengers__input--search"
                    type="search"
                    name="search"
                    placeholder="Пошук"
                    onChange={(e) => onSearch(e.target.value)}
                  />
                </div>
                <button className="passengers__btn--add" type="button" onClick={() => setModal("PassengerAddForm")}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="21" height="20" viewBox="0 0 21 20" fill="none" >
                    <path d="M10.4998 4.16663V15.8333M4.6665 9.99996H16.3332" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  Додати нового пасажира
                </button>
              </div>
              <AuthBox isSuperAdmin={isSuperAdmin} />
            </div>
            <div className="passengers-content__body">
              <PassengersList passengersList={passengersList} selectedPassengersIds={selectedPassengersIds} setSelectedPassengersIds={setSelectedPassengersIds} totalPassengers={totalPassengers} totalTickets={totalTickets} />
            </div>
            <div className="passengers-content__footer">
              <Pagination currentPage={page} totalPages={totalPages} setNewPage={setNewPage} setNewLimit={setNewLimit} />
              <div className="passengers__btn--generate-wrap">
                <button className="passengers__btn--generate passengers__btn--generate-list" disabled={isGenerateListBtnDisabled} onClick={generatePassengersList}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="15" height="18" viewBox="0 0 15 18" fill="none">
                    <path d="M4.61111 2.0777L14 2.07853M4.61111 9.00081L14 9.00173M4.61111 15.9239L14 15.9248M1.36111 2.07692H1.36833M1.36111 9H1.36833M1.36111 15.9231H1.36833M1.72222 2.07692C1.72222 2.39555 1.56055 2.65385 1.36111 2.65385C1.16168 2.65385 1 2.39555 1 2.07692C1 1.7583 1.16168 1.5 1.36111 1.5C1.56055 1.5 1.72222 1.7583 1.72222 2.07692ZM1.72222 9C1.72222 9.31858 1.56055 9.57692 1.36111 9.57692C1.16168 9.57692 1 9.31858 1 9C1 8.68142 1.16168 8.42308 1.36111 8.42308C1.56055 8.42308 1.72222 8.68142 1.72222 9ZM1.72222 15.9231C1.72222 16.2417 1.56055 16.5 1.36111 16.5C1.16168 16.5 1 16.2417 1 15.9231C1 15.6045 1.16168 15.3462 1.36111 15.3462C1.56055 15.3462 1.72222 15.6045 1.72222 15.9231Z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Сформувати список
                </button>
                <button className="passengers__btn--generate passengers__btn--generate-ticket" disabled={isGenerateTicketBtnDisabled} onClick={generateTickets}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none" >
                    <path d="M6.66663 14.1667L9.99996 17.5M9.99996 17.5L13.3333 14.1667M9.99996 17.5V10M16.6666 13.9524C17.6845 13.1117 18.3333 11.8399 18.3333 10.4167C18.3333 7.88536 16.2813 5.83333 13.75 5.83333C13.5679 5.83333 13.3975 5.73833 13.3051 5.58145C12.2183 3.73736 10.212 2.5 7.91663 2.5C4.46485 2.5 1.66663 5.29822 1.66663 8.75C1.66663 10.4718 2.36283 12.0309 3.48908 13.1613" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                    Сформувати квиток
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
      <Modal showModal={showModal} setShowModal={setShowModal}>
        {compareStrings(modalType, 'PassengerAddForm') && <PassengerAddForm closeModal={setModal} />}
        {compareStrings(modalType, 'PassengerEditForm') && <PassengerEditForm data={selectedPassengerData} closeModal={setModal} />}
        {compareStrings(modalType, 'PassengersDeleteModal') && <SubmitMsg text={submitMsgText} handleCancel={() => setModal()} handleSubmit={onPassengersDelete} />}
      </Modal>
    </>
  );
};

export default PassengersPage;

// Функція для перетворення дати з формату YYYY-MM-DD у DD.MM.YYYY
function formatDateForInput (dateString) {
  const [year, month, day] = dateString.split("-");
  return `${day}.${month}.${year}`;
};
